import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  ADD_MENU_ITEM_INGREDIENT_MUTATION,
  EDIT_MENU_ITEM_INGREDIENT_MUTATION,
  MENU_INFO_CONTEXT_QUERY,
  REMOVE_MENU_ITEM_INGREDIENT_METADATA_MUTATION,
  SET_MENU_ITEM_INGREDIENT_PORTION_MUTATION,
  SET_MENU_ITEM_PLATE_PRICE_MUTATION,
} from './menu-info-context-queries';
import { DataProxy, useMutation } from '@apollo/client';
import {
  EditIngredientFields,
  ManualIngredientFields,
  MenuInfo_AddMenuItemIngredientMutation,
  MenuInfo_AddMenuItemIngredientMutationVariables,
  MenuInfo_EditMenuItemIngredientMutation,
  MenuInfo_EditMenuItemIngredientMutationVariables,
  MenuInfo_MenuInfoContextQuery,
  MenuInfo_MenuInfoContextQueryVariables,
  MenuInfo_MenuItemFieldsFragment,
  MenuInfo_RemoveMenuItemIngredientMetadataMutation,
  MenuInfo_RemoveMenuItemIngredientMetadataMutationVariables,
  MenuInfo_SetIngredientPortionMutation,
  MenuInfo_SetIngredientPortionMutationVariables,
  MenuInfo_SetPlatePriceMutation,
  MenuInfo_SetPlatePriceMutationVariables,
  VariantPackInfoContext_GroupBadgeFieldsFragment,
} from '@/types';
import { getVariantPackGroupParentUUIDs } from '@/utils/category';
import { ItemListOptionMetadata } from './item-context-types';
import { ListOption } from '@/components/list-item';
import { ManualIngredientMetadata } from '@/components/menu/search/menu-item-search-types';
import {
  PACK_UNIT_TO_UNIT_TYPE_MAP,
  PortionUnit,
  STANDARD_VOLUME_UNIT_CONVERSION_MAP,
  STANDARD_WEIGHT_UNIT_CONVERSION_MAP,
  UnitType,
} from '@/utils/menu';
import { PackUnit } from '@/enums';
import { useAuthenticatedUserContext } from './user-context';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useDebouncedCallback } from 'use-debounce';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useToasts } from './toast-context';
import {
  useVariantPackInfoContext,
  VariantPackInfoType,
} from './variant-pack-info-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export type IngredientSearchResultMetadataType =
  | ItemListOptionMetadata
  | ManualIngredientMetadata;

interface MenuItemIngredientPortion {
  portionQuantity?: number | null;
  portionUnit?: PortionUnit;
}

export interface MenuItemIngredient extends MenuItemIngredientPortion {
  badges?: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  basePackUnit?: PackUnit;
  costInMicros: number;
  externalItemUUID?: string | null;
  ingredientMetadataUUID: string;
  ingredientName: string;
  ingredientUUID: string;
  originalBaseUnit?: PackUnit;
  packUnit: PackUnit;
  photoURLs?: string[];
  unitCount: number;
  variantPackUUID?: string;
}
export interface MenuItem {
  ingredients: MenuItemIngredient[];
  name: string;
  plateCostInMicros?: number;
  platePriceInMicros?: number;
  uuid: string;
}

export type MenuItemListOption = ListOption<MenuItem>;
type MenuItemMap = Map<string, MenuItem>;
interface NewMenuItemIngredient {
  ingredientUUID?: string;
  manualIngredient?: ManualIngredientFields;
  variantPackCostInMicros?: number;
  variantPackUUID?: string;
}
type UpdateCacheFn = (
  cachedMenuItems: MenuInfo_MenuItemFieldsFragment[],
) => MenuInfo_MenuItemFieldsFragment[];

export interface MenuInfoContextType {
  addMenuItemIngredient: (
    ingredient: NewMenuItemIngredient,
    menuItemUUID: string,
    menuItemIngredientUUID?: string,
  ) => void;
  editMenuItemIngredient: (
    menuItemIngredientUUID: string,
    ingredientFields: EditIngredientFields,
  ) => void;
  loading: boolean;
  menuItemMap: MenuItemMap;
  removeMenuItemIngredient: (
    ingredientUUID: string,
    menuItemUUID: string,
  ) => void;
  setMenuItemIngredientPortion: (
    portion: MenuItemIngredientPortion,
    ingredientUUID: string,
    menuItemUUID: string,
  ) => void;
  setMenuItemPrice: (priceInMicros: number, menuItemUUID: string) => void;
  updateCachedMenuItems: (
    client: DataProxy,
    updateCacheFn: UpdateCacheFn,
  ) => void;
}

const MENU_ITEM_DEBOUNCE_DELAY_MS = 250;

export const calculatePortionCostInMicros = (
  ingredient: MenuItemIngredient,
  newQuantity?: number | null,
  newUnit?: PortionUnit,
) => {
  // Determine the portion quantity and unit, defaulting to the ingredient's current values if not provided
  const portionQuantity = newQuantity ?? ingredient.portionQuantity;
  const portionUnit = newUnit ?? ingredient.portionUnit;
  const packUnit = ingredient.basePackUnit ?? ingredient.packUnit;

  // Return 0 if the portion quantity or unit is invalid
  if (typeof portionQuantity !== 'number' || typeof portionUnit !== 'string') {
    return 0;
  }

  // Map the pack unit to its corresponding unit type (Volume or Weight)
  const unitType = PACK_UNIT_TO_UNIT_TYPE_MAP[packUnit];
  let standardizedUnitMap = null;

  // Select the appropriate conversion map based on the unit type
  switch (unitType) {
    case UnitType.Volume:
      standardizedUnitMap = STANDARD_VOLUME_UNIT_CONVERSION_MAP;
      break;
    case UnitType.Weight:
      standardizedUnitMap = STANDARD_WEIGHT_UNIT_CONVERSION_MAP;
      break;
    default:
      break;
  }

  // Get the conversion factor for the portion unit to a standardized unit
  const standardizedUnitTypeConversion =
    // @ts-ignore mapping is guaranteed by ALLOWED_PACK_UNITS_FOR_CONVERSION
    standardizedUnitMap?.[portionUnit] ?? 1;

  let conversionFactor = 1;

  // Calculate the conversion factor for volume units
  if (unitType === UnitType.Volume) {
    // Format the base pack unit to match the expected key format in the PortionUnit map
    const basePackUnitKey = (packUnit.charAt(0).toUpperCase() +
      packUnit.slice(1).toLowerCase()) as keyof typeof PortionUnit;

    // Retrieve the original portion unit key from the map using the above basePackUnitKey
    const originalPortionUnitKey = PortionUnit[
      basePackUnitKey
    ] as keyof typeof standardizedUnitMap;

    // Get the conversion factor for the original portion unit from the standardized unit map
    const portionUnitConversion =
      standardizedUnitMap?.[originalPortionUnitKey] ?? 1;

    // Calculate the conversion factor by dividing the standardized unit type conversion by the portion unit conversion
    conversionFactor = standardizedUnitTypeConversion / portionUnitConversion;
  } else {
    // Use the standardized conversion directly for non-volume units
    conversionFactor = standardizedUnitTypeConversion;
  }

  // Calculate and return the cost in micros for the given portion
  return parseFloat(
    (portionQuantity * conversionFactor * ingredient.costInMicros).toFixed(2),
  );
};

export const menuItemIngredientEditable = (ingredient: MenuItemIngredient) => {
  const isCustomIngredient = !ingredient.variantPackUUID;
  // Allow editing if pack details are missing or overridden
  const missingPackDetails =
    !ingredient.originalBaseUnit ||
    !ingredient.basePackUnit ||
    !ingredient.unitCount;

  const oneEachPerCase =
    ingredient.packUnit === PackUnit.Case &&
    ingredient.unitCount === 1 &&
    ingredient.basePackUnit === PackUnit.Each;

  return isCustomIngredient || missingPackDetails || oneEachPerCase;
};

export const MenuInfoContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const itemQueryVariables = useItemQueryVariables();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { getVariantPackInfo, parentVariantPackGroupMap, variantPackGroupMap } =
    useVariantPackInfoContext();
  const { showErrorToast } = useToasts();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const handleFocus = useCallback(() => {
    setHasBeenFocused(true);
  }, []);
  useComponentFocus({ onFocus: handleFocus });

  const { data, loading } = useWrappedQuery<
    MenuInfo_MenuInfoContextQuery,
    MenuInfo_MenuInfoContextQueryVariables
  >(MENU_INFO_CONTEXT_QUERY, {
    skip: !hasBeenFocused,
    variables: {
      ...itemQueryVariables,
      restaurantUUID,
      supplierUUID,
    },
  });

  const [addIngredient] = useMutation<
    MenuInfo_AddMenuItemIngredientMutation,
    MenuInfo_AddMenuItemIngredientMutationVariables
  >(ADD_MENU_ITEM_INGREDIENT_MUTATION);

  const [editIngredient] = useMutation<
    MenuInfo_EditMenuItemIngredientMutation,
    MenuInfo_EditMenuItemIngredientMutationVariables
  >(EDIT_MENU_ITEM_INGREDIENT_MUTATION);

  const [removeIngredient] = useMutation<
    MenuInfo_RemoveMenuItemIngredientMetadataMutation,
    MenuInfo_RemoveMenuItemIngredientMetadataMutationVariables
  >(REMOVE_MENU_ITEM_INGREDIENT_METADATA_MUTATION);

  const [updateIngredientPortion] = useMutation<
    MenuInfo_SetIngredientPortionMutation,
    MenuInfo_SetIngredientPortionMutationVariables
  >(SET_MENU_ITEM_INGREDIENT_PORTION_MUTATION);

  const [updatePlatePrice] = useMutation<
    MenuInfo_SetPlatePriceMutation,
    MenuInfo_SetPlatePriceMutationVariables
  >(SET_MENU_ITEM_PLATE_PRICE_MUTATION);

  const menuItemMap: MenuItemMap = useMemo(() => {
    return (data?.menu_items || []).reduce((acc, mi) => {
      let plateCostInMicros = 0;
      const ingredients = mi.menu_item_ingredient_metadata.map(
        (ingredientMetadata) => {
          const ing = ingredientMetadata.menu_item_ingredient;
          const variantPack = ing.variant_pack;
          const variantPackUUID = variantPack?.uuid;
          const variantPackInfo: VariantPackInfoType | null = variantPackUUID
            ? getVariantPackInfo(variantPackUUID)
            : null;
          const variantPackGroupCategoryUUIDs = itemGroupsEnabled
            ? getVariantPackGroupParentUUIDs(
                (variantPack?.variant_pack_group_memberships || []).map(
                  (member) => member.variant_pack_group_uuid,
                ),
                parentVariantPackGroupMap,
              )
            : [];
          // Create a unique set of badges by extracting them from variantPackGroupMap,
          // ensuring that each badge appears only once based on its uuid
          const badges = [
            ...new Map(
              variantPackGroupCategoryUUIDs
                .flatMap((uuid) => variantPackGroupMap[uuid]?.badges || [])
                .map((badge) => [badge.uuid, badge]),
            ).values(),
          ];
          /*
           * Components and helper functions expect costInMicros to be the per-unit price.
           * VariantPackInfo maintains priceInMicros as the per-unit price. Because the unit
           * count can be overridden when saving a custom ingredient, adjust the priceInMicros
           * by the original unit count to get the total price, then divide by the new unit count
           * to get the per-unit price.
           */
          const variantPackPricePerUnit =
            typeof variantPackInfo?.priceInMicros === 'number'
              ? (variantPackInfo.priceInMicros * variantPackInfo.unitCount) /
                ing.pack.unit_count
              : 0;

          /*
           * Custom ingredients maintain costInMicros as the total cost. Divide by the
           * unit count to get the per-unit price.
           */
          const customIngredientCostPerUnit = ing.cost_in_micros
            ? ing.cost_in_micros / ing.pack.unit_count
            : 0;

          const costInMicros = variantPackUUID
            ? variantPackPricePerUnit
            : customIngredientCostPerUnit;

          const ingredient: MenuItemIngredient = {
            badges,
            basePackUnit: ing.pack.base_unit as PackUnit,
            costInMicros,
            externalItemUUID:
              ingredientMetadata.menu_item_ingredient.variant_pack
                ?.external_item_id,
            ingredientMetadataUUID: ingredientMetadata.uuid,
            ingredientName: ing.ingredient_name,
            ingredientUUID: ing.uuid,
            originalBaseUnit: ing.variant_pack?.pack?.base_unit as PackUnit,
            packUnit: ing.pack.unit as PackUnit,
            photoURLs: ing.variant_pack?.item.photo_url_list,
            portionQuantity:
              (ingredientMetadata.ingredient_metadata
                .portion_quantity as number) ?? null,
            portionUnit:
              (ingredientMetadata.ingredient_metadata
                .portion_unit as PortionUnit) ?? null,
            unitCount: ing.pack.unit_count,
            variantPackUUID,
          };
          plateCostInMicros += calculatePortionCostInMicros(ingredient);

          return ingredient;
        },
      );

      acc.set(mi.uuid, {
        ingredients,
        name: mi.item_name,
        plateCostInMicros: plateCostInMicros || undefined,
        platePriceInMicros: mi.price_in_micros || undefined,
        uuid: mi.uuid,
      });

      return acc;
    }, new Map());
  }, [
    itemGroupsEnabled,
    data,
    getVariantPackInfo,
    parentVariantPackGroupMap,
    variantPackGroupMap,
  ]);

  const updateCachedMenuItems = useCallback(
    (client: DataProxy, updateCacheFn: UpdateCacheFn) => {
      try {
        const variables = {
          ...itemQueryVariables,
          restaurantUUID,
          supplierUUID,
        };

        const menuInfoContextQuery = client.readQuery<
          MenuInfo_MenuInfoContextQuery,
          MenuInfo_MenuInfoContextQueryVariables
        >({
          query: MENU_INFO_CONTEXT_QUERY,
          variables,
        });

        if (!menuInfoContextQuery) {
          return;
        }

        client.writeQuery<MenuInfo_MenuInfoContextQuery>({
          data: {
            ...menuInfoContextQuery,
            menu_items: updateCacheFn(menuInfoContextQuery.menu_items),
          },
          query: MENU_INFO_CONTEXT_QUERY,
          variables,
        });
      } catch {
        // Ignore cache update errors
      }
    },
    [itemQueryVariables, restaurantUUID, supplierUUID],
  );

  const addMenuItemIngredient = useCallback(
    async (
      ingredient: NewMenuItemIngredient,
      menuItemUUID: string,
      menuItemIngredientUUID?: string,
    ) => {
      try {
        await addIngredient({
          update: (client, { data: addMutationData }) => {
            const newMenuItemIngredient =
              addMutationData?.addMenuItemIngredient.menu_item_ingredient;

            if (newMenuItemIngredient) {
              const metadataForMenuItem =
                newMenuItemIngredient.menu_item_ingredient_metadata.filter(
                  (ingredientMetadata) => {
                    return ingredientMetadata.menu_item_uuid === menuItemUUID;
                  },
                );
              updateCachedMenuItems(client, (menuItems) =>
                menuItems.map((mi) =>
                  mi.uuid === menuItemUUID
                    ? {
                        ...mi,
                        menu_item_ingredient_metadata: [
                          ...mi.menu_item_ingredient_metadata,
                          ...metadataForMenuItem,
                        ],
                      }
                    : mi,
                ),
              );
            }
          },
          variables: {
            ...ingredient,
            ...itemQueryVariables,
            menuItemIngredientUUID,
            menuItemUUID,
            restaurantUUID,
            supplierUUID,
          },
        });
      } catch {
        showErrorToast();
      }
    },
    [
      addIngredient,
      itemQueryVariables,
      restaurantUUID,
      showErrorToast,
      supplierUUID,
      updateCachedMenuItems,
    ],
  );

  const editMenuItemIngredient = useCallback(
    async (
      menuItemIngredientUUID: string,
      ingredientFields: EditIngredientFields,
    ) => {
      try {
        await editIngredient({
          variables: {
            ...itemQueryVariables,
            ingredientFields,
            menuItemIngredientUUID,
            restaurantUUID,
            supplierUUID,
          },
        });
      } catch {
        showErrorToast();
      }
    },
    [
      editIngredient,
      itemQueryVariables,
      restaurantUUID,
      showErrorToast,
      supplierUUID,
    ],
  );

  const removeMenuItemIngredient = useCallback(
    async (ingredientMetadataUUID: string, menuItemUUID: string) => {
      try {
        await removeIngredient({
          update: (client, { data: removeMutationData }) => {
            const removedMenuItemIngredientMetadataUUID =
              removeMutationData?.delete_menu_item_ingredient_metadata_by_pk
                ?.uuid;

            if (removedMenuItemIngredientMetadataUUID) {
              updateCachedMenuItems(client, (menuItems) =>
                menuItems.map((mi) =>
                  mi.uuid === menuItemUUID
                    ? {
                        ...mi,
                        menu_item_ingredient_metadata:
                          mi.menu_item_ingredient_metadata.filter(
                            (ingredientMetadata) =>
                              ingredientMetadata.uuid !==
                              removedMenuItemIngredientMetadataUUID,
                          ),
                      }
                    : mi,
                ),
              );
            }
          },
          variables: {
            ingredientMetadataUUID,
          },
        });
      } catch {
        showErrorToast();
      }
    },
    [removeIngredient, showErrorToast, updateCachedMenuItems],
  );

  const debouncedSetMenuItemIngredientPortion = useDebouncedCallback(
    async (
      portion: MenuItemIngredientPortion,
      ingredientUUID: string,
      menuItemUUID: string,
    ) => {
      try {
        const formattedMetadata = {
          portion_quantity: portion.portionQuantity,
          portion_unit: portion.portionUnit,
        };

        await updateIngredientPortion({
          update: (client, { data: setIngredientPortionData }) => {
            const updatedIngredient =
              setIngredientPortionData?.update_menu_item_ingredient_metadata_by_pk;

            if (updatedIngredient) {
              updateCachedMenuItems(client, (menuItems) =>
                menuItems.map((mi) =>
                  mi.uuid === menuItemUUID
                    ? {
                        ...mi,
                        menu_item_ingredient_metadata:
                          mi.menu_item_ingredient_metadata.map(
                            (ingredientMetadata) =>
                              ingredientMetadata.menu_item_ingredient.uuid ===
                              ingredientUUID
                                ? {
                                    ...ingredientMetadata,
                                    ingredient_metadata:
                                      updatedIngredient.ingredient_metadata,
                                  }
                                : ingredientMetadata,
                          ),
                      }
                    : mi,
                ),
              );
            }
          },
          variables: {
            ingredientUUID,
            metadata: formattedMetadata,
          },
        });
      } catch {
        showErrorToast();
      }
    },
    MENU_ITEM_DEBOUNCE_DELAY_MS,
  );

  const debouncedSetMenuItemPrice = useDebouncedCallback(
    async (priceInMicros: number, menuItemUUID: string) => {
      try {
        await updatePlatePrice({
          update: (client, { data: setPriceData }) => {
            const newPriceInMicros =
              setPriceData?.update_menu_items_by_pk?.price_in_micros;

            if (newPriceInMicros) {
              updateCachedMenuItems(client, (menuItems) =>
                menuItems.map((mi) =>
                  mi.uuid === menuItemUUID
                    ? {
                        ...mi,
                        price_in_micros: newPriceInMicros,
                      }
                    : mi,
                ),
              );
            }
          },
          variables: {
            menuItemUUID,
            priceInMicros,
          },
        });
      } catch {
        showErrorToast();
      }
    },
    MENU_ITEM_DEBOUNCE_DELAY_MS,
  );

  const value = useMemo(() => {
    return {
      addMenuItemIngredient,
      editMenuItemIngredient,
      loading,
      menuItemMap,
      removeMenuItemIngredient,
      setMenuItemIngredientPortion: debouncedSetMenuItemIngredientPortion,
      setMenuItemPrice: debouncedSetMenuItemPrice,
      updateCachedMenuItems,
    };
  }, [
    addMenuItemIngredient,
    debouncedSetMenuItemIngredientPortion,
    debouncedSetMenuItemPrice,
    editMenuItemIngredient,
    loading,
    menuItemMap,
    removeMenuItemIngredient,
    updateCachedMenuItems,
  ]);

  return (
    <MenuInfoContext.Provider value={value}>
      {children}
    </MenuInfoContext.Provider>
  );
};

export const MenuInfoContext = createContext<MenuInfoContextType | null>(null);

export const useMenuInfoContext = () => {
  const context = useContext(MenuInfoContext);
  if (!context) {
    throw new Error(
      'useMenuInfoContext must be used within a MenuInfoContextProvider',
    );
  }
  return context;
};
